import React from 'react';
import BaseEChart from './BaseEChart';
import { CONSENSUS_PALETTE, CHART_FONT_FAMILY } from './chartConstants';
import { formatNumber } from '@utils/format';
import type { ChartOption } from '@app-types/chart';
import type { ConsensusSegment } from '@app-types/performance';

export interface ConsensusDonutChartProps {
  segments: ConsensusSegment[];
  analystCount: number | null;
  sentiment: string;
  height?: number;
  onSegmentHover?: (segment: ConsensusDonutHoverSegment | null) => void;
}

export interface ConsensusDonutHoverSegment {
  key: string;
  label: string;
  color: string;
}

const DONUT_TEXT_WIDTH = 87.10391235351562;
const DONUT_TEXT_TOP = 43.2304541015625;

const toChartValue = (value: number | null) =>
  typeof value === 'number' && Number.isFinite(value) ? value : 0;

/**
 * Consensus donut chart. Center text is drawn with ECharts graphic so the
 * count and analyst label stay in the Figma donut hole position.
 * The legend list (labels + counts) is rendered outside the chart by the
 * section component, so ECharts' own legend is disabled (see T07).
 */
const ConsensusDonutChart: React.FC<ConsensusDonutChartProps> = ({
  segments,
  analystCount,
  sentiment,
  height = 141,
  onSegmentHover,
}) => {
  const handleSegmentMouseOver = (params: unknown) => {
    if (!onSegmentHover) {
      return;
    }

    const { componentType, dataIndex } = params as {
      componentType?: string;
      dataIndex?: number;
    };
    if (componentType !== 'series' || typeof dataIndex !== 'number') {
      return;
    }

    const segment = segments[dataIndex];
    if (!segment) {
      return;
    }

    onSegmentHover({
      key: segment.key,
      label: segment.label,
      color: CONSENSUS_PALETTE[dataIndex % CONSENSUS_PALETTE.length],
    });
  };

  const handleSegmentMouseOut = () => {
    onSegmentHover?.(null);
  };

  const option: ChartOption = {
    tooltip: { show: false },
    graphic: {
      elements: [
        {
          type: 'group',
          left: 'center',
          top: DONUT_TEXT_TOP,
          z: 10,
          silent: true,
          children: [
            {
              type: 'text',
              style: {
                text: formatNumber(analystCount, 0),
                x: DONUT_TEXT_WIDTH / 2,
                y: 14,
                width: DONUT_TEXT_WIDTH,
                align: 'center',
                verticalAlign: 'middle',
                fill: '#000000',
                fontFamily: CHART_FONT_FAMILY,
                fontSize: 18,
                fontWeight: 700,
              },
            },
            {
              type: 'text',
              style: {
                text: 'アナリスト',
                x: DONUT_TEXT_WIDTH / 2,
                y: 35,
                width: DONUT_TEXT_WIDTH,
                align: 'center',
                verticalAlign: 'middle',
                fill: '#484848',
                fontFamily: CHART_FONT_FAMILY,
                fontSize: 12,
                fontWeight: 500,
              },
            },
          ],
        },
      ],
    },
    series: [
      {
        type: 'pie',
        radius: ['62%', '90%'],
        center: ['50%', '50%'],
        avoidLabelOverlap: false,
        label: { show: false },
        labelLine: { show: false },
        emphasis: {
          scale: true,
          scaleSize: 5,
        },
        data: segments.map((s, i) => ({
          name: s.label,
          value: toChartValue(s.count),
          itemStyle: { color: CONSENSUS_PALETTE[i % CONSENSUS_PALETTE.length] },
        })),
      },
    ],
  };

  return (
    <BaseEChart
      option={option}
      height={height}
      onEvents={{
        mouseover: handleSegmentMouseOver,
        mouseout: handleSegmentMouseOut,
        globalout: handleSegmentMouseOut,
      }}
      ariaLabel={`アナリストコンセンサス（${formatNumber(analystCount, 0)}名、判定: ${sentiment}）`}
    />
  );
};

export default ConsensusDonutChart;
